import './StageShell.scss';
import React, { FC, ReactNode } from 'react';
import type {
    Realm as RealmFields,
    Zone as ZoneFields,
    Matrix as MatrixFields,
} from '@/generated/graphql';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import SEO from '@/components/extra/SEO/SEO';
import classNames from 'classnames';
import Photo, { getSrc } from '@/components/media/Photo/Photo';

type StageShellProps = {
    componentName: 'Realm' | 'Zone' | 'Matrix';
    className?: string | string[];
    fields: RealmFields | ZoneFields | MatrixFields;
    canonicalPath?: string[];
    children: ReactNode[];
};

const StageShell: FC<StageShellProps> = ({
    componentName,
    className,
    fields,
    canonicalPath,
    children,
}) => {
    const { userCurrent } = useUserCurrent();

    const blockClassNames = ['Stage'];
    blockClassNames.push(
        componentName,
        ...(Array.isArray(className) ? className : [className]),
    );

    const TagName = componentName === 'Matrix' ? 'article' : 'section';

    const bannerText =
        componentName === 'Matrix' && 'leadText' in fields
            ? fields.leadText
            : fields.bannerText;

    const bannerImage = fields.bannerPhoto[0];
    const ogImage = bannerImage
        ? getSrc(bannerImage.url, bannerImage.fileChanged, 'm')
        : undefined;
    const ogImageAlt = bannerImage?.caption;

    return (
        <TagName className={blockClassNames.join(' ')}>
            <SEO
                title={fields.title}
                canonicalPath={canonicalPath}
                description={fields.bannerText}
                image={ogImage}
                imageAlt={ogImageAlt}
            />
            <header className={classNames('Stage_head', {})}>
                <h1
                    className={classNames('Stage_title', {
                        'Stage_title-unSticky': userCurrent?.isContentEditor,
                    })}
                >
                    {userCurrent?.isContentEditor &&
                        userCurrent?.isEditMode && (
                            <span className="Stage_component">
                                {componentName}
                            </span>
                        )}
                    {fields.title}
                </h1>
                <Photo
                    className="Stage_bannerPhoto"
                    fields={bannerImage}
                    imageSize="s"
                />
                <p className="Stage_bannerText">{bannerText}</p>
            </header>
            <section className="Stage_body">{children}</section>
        </TagName>
    );
};

export default StageShell;
